import {convertAuthTokenDtoToModel} from '../../../core/api/converters/convert-auth-token-dto-to-model';
import {AuthTokenDto} from '../../../core/api/dto/auth-token.dto';
import {writeAuthTokenToLocalStorage} from './auth-token.utils';

/**
 * Single sign-on from the Purolator Automation Sites landing page, which signs in against the same
 * /Authenticate/login endpoint. Local storage is per host, so the landing page opens this site as
 * `/#sso=<base64url JSON of its login response>`. The fragment is never sent to a server.
 * src/sso.ts calls consumeSsoHandoff() before the app loads: it stores the token the way a login
 * does and removes it from the address bar and history. The API verifies the token as for any login.
 */

/** The login response carried in an `#sso=` fragment, or null if there is none or it is unusable. */
export function readSsoHandoff(hash: string, now = new Date()): AuthTokenDto | null {
  const match = /^#sso=([A-Za-z0-9_-]+)$/.exec(hash);
  if (!match) {
    return null;
  }
  try {
    const base64 = match[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), c => c.charCodeAt(0));
    const dto = JSON.parse(new TextDecoder().decode(bytes));
    if (typeof dto?.access_token !== 'string' || dto.access_token.split('.').length !== 3) {
      return null;
    }
    const expires = new Date(dto.expires);
    return expires > now ? (dto as AuthTokenDto) : null;
  } catch {
    return null;
  }
}

/** Takes the token from an `#sso=` fragment, if any, and opens the home page signed in. */
export function consumeSsoHandoff(location: Location = window.location, history: History = window.history): void {
  if (!location.hash.startsWith('#sso=')) {
    return;
  }
  const dto = readSsoHandoff(location.hash);
  if (dto) {
    writeAuthTokenToLocalStorage(convertAuthTokenDtoToModel({token_type: 'Bearer', ...dto}));
  }
  // Drop the token from the address bar and history whether or not it was usable.
  history.replaceState(null, '', dto ? '/home' : location.pathname + location.search);
}
